import React, { useMemo } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { ChevronRightIcon, IconBadge } from '@saas-ui/react';
import { Contact2, User } from 'lucide-react';

import { Card, CardHeader, CardTitle, IconButton } from '@/components/core';
import { EngineerIcon, FONTS_OPACITY, JobIcon } from '@/constant';
import { JOB_STATUS } from '@/types/jobs';
import { AutocompleteCandidate, AutocompleteEntityType } from '@/types/search';
import { getJobStatusCategoryColors, getJobStatusLabel } from '@/utils/job';

const TYPE_ICONS: Record<
  Extract<AutocompleteEntityType, 'contact' | 'person' | 'engineer' | 'job'>,
  React.ElementType
> = {
  contact: Contact2,
  person: User,
  engineer: EngineerIcon,
  job: JobIcon,
};

interface ResultCardProps {
  candidate: AutocompleteCandidate;
  onSelect: (candidate: AutocompleteCandidate) => void;
}

const isJobStatus = (value: string): value is JOB_STATUS => {
  return Object.values(JOB_STATUS).includes(value as JOB_STATUS);
};

export const ResultCard: React.FC<ResultCardProps> = ({
  candidate,
  onSelect,
}) => {
  const IconComponent =
    TYPE_ICONS[candidate.type as keyof typeof TYPE_ICONS] ?? Contact2;

  const statusLabel = useMemo(() => {
    const raw = candidate.extra?.status;
    if (typeof raw !== 'string' || !raw) return null;
    if (isJobStatus(raw)) return getJobStatusLabel(raw);
    return raw;
  }, [candidate.extra?.status]);

  const statusColor = useMemo(() => {
    const raw = candidate.extra?.status;
    if (typeof raw === 'string' && isJobStatus(raw)) {
      return getJobStatusCategoryColors(raw).color;
    }
    return 'tags.blue.font';
  }, [candidate.extra?.status]);

  const showMatchedVia =
    Boolean(candidate.matchedVia) && candidate.matchedVia !== 'name';

  return (
    <Card
      px={0}
      py={4}
      rounded='2xl'
      color='white'
      cursor='pointer'
      bg='cards.bg'
      border='1px solid'
      borderColor='border.light'
      overflow='hidden'
      className='group'
      data-cy='global-search-result'
      onClick={() => onSelect(candidate)}
    >
      <CardHeader
        py={0}
        px={4}
        action={
          <IconButton
            icon={<ChevronRightIcon boxSize='16px' />}
            aria-label={`Open ${candidate.label}`}
            boxSize='36px'
            variant='glassy'
            _groupHover={{
              bg: 'glass-items.bg-hover',
            }}
            onClick={event => {
              event.stopPropagation();
              onSelect(candidate);
            }}
          />
        }
      >
        <HStack align='flex-start' gap={2.5} w='full' minW={0}>
          <Box flexShrink={0}>
            <IconBadge
              icon={<IconComponent width='16px' height='16px' />}
              variant='solid'
              colorScheme='whiteAlpha'
              isRound
              boxSize='36px'
              backgroundColor='glass-items.bg-active'
              _groupHover={{
                backgroundColor: 'cards.bg-hover',
              }}
            />
          </Box>
          <Stack flex={1} gap={0} minW={0} maxW='100%'>
            <CardTitle
              fontSize='sm'
              lineHeight='5'
              fontWeight='normal'
              noOfLines={1}
            >
              {candidate.label}
            </CardTitle>
            {candidate.sublabel ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={0.5}
                noOfLines={{ base: 2, xl: 1 }}
                color='fonts.primary'
              >
                {candidate.sublabel}
              </Text>
            ) : null}
            {statusLabel ? (
              <HStack gap={1.5} minW={0}>
                <Box
                  w={1.5}
                  h={1.5}
                  rounded='full'
                  bg={statusColor}
                  flexShrink={0}
                />
                <Text
                  fontSize='xs'
                  lineHeight='5'
                  fontWeight='semibold'
                  color={statusColor}
                  textTransform='uppercase'
                  noOfLines={1}
                >
                  {statusLabel}
                </Text>
              </HStack>
            ) : null}
            {showMatchedVia ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={FONTS_OPACITY}
                noOfLines={1}
                color='fonts.primary'
              >
                via {candidate.matchedVia?.replace(/_/g, ' ')}
              </Text>
            ) : null}
          </Stack>
        </HStack>
      </CardHeader>
    </Card>
  );
};
